import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, FileText, ArrowRight, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { nexusApi } from "@/context/NexusAuthContext";
import { STATUS_META, categoryLabel, regionLabel, localizeListing } from "@/pages/nexus/meta";
import ListingCard from "@/components/nexus/ListingCard";
import useSEO, { canonicalFor, breadcrumbLd } from "@/lib/useSEO";
import "@/pages/nexus/nexus.css";

export default function ListingDetail() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ndaOpen, setNdaOpen] = useState(false);

  const l = data?.listing && localizeListing(data.listing, i18n.language);
  useSEO(
    l
      ? {
          title: `${l.title} — Corpora Nexus`,
          description: l.teaser_summary,
          canonical: canonicalFor(`/nexus/listings/${l.slug}`),
          image: canonicalFor("/brand/nexus-og.jpg"),
          jsonLd: [
            {
              "@context": "https://schema.org",
              "@type": "Product",
              name: l.title,
              category: l.category,
              description: l.teaser_summary,
              brand: { "@type": "Organization", name: "Corpora Nexus" },
              offers: {
                "@type": "Offer",
                availability:
                  l.status_badge === "under_offer"
                    ? "https://schema.org/InStock"
                    : l.status_badge === "recently_sold"
                    ? "https://schema.org/SoldOut"
                    : "https://schema.org/InStock",
                priceCurrency: "EUR",
                price: "0",
                priceSpecification: {
                  "@type": "PriceSpecification",
                  valueAddedTaxIncluded: false,
                  price: "On application",
                },
              },
              additionalProperty: [
                { "@type": "PropertyValue", name: "Jurisdiction", value: l.jurisdiction },
                { "@type": "PropertyValue", name: "Regulator", value: l.regulator || "—" },
                { "@type": "PropertyValue", name: "Status", value: l.regulatory_status || l.status_badge },
              ],
            },
            breadcrumbLd([
              { name: "Corporaco", url: canonicalFor("/") },
              { name: "Nexus", url: canonicalFor("/nexus") },
              { name: "Marketplace", url: canonicalFor("/nexus/listings") },
              { name: l.title, url: canonicalFor(`/nexus/listings/${l.slug}`) },
            ]),
          ],
        }
      : {}
  );

  useEffect(() => {
    setLoading(true);
    nexusApi
      .get(`/listings/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="nx-container py-24 text-white/50 font-mono text-xs">{t("nexus.listing.loading")}</div>;
  if (!data)
    return (
      <div className="nx-container py-24 text-center">
        <div className="font-serif text-2xl text-white mb-4">{t("nexus.listing.notFound")}</div>
        <Link to="/nexus/listings" className="nx-btn-secondary">
          {t("nexus.listing.browseMarketplace")}
        </Link>
      </div>
    );

  const status = STATUS_META[l.status_badge] || STATUS_META.available;

  return (
    <div data-testid={`listing-detail-${slug}`}>
      <div className="nx-container pt-8">
        <Link
          to="/nexus/listings"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-white/60 hover:text-white mb-8"
        >
          <ArrowLeft size={12} /> {t("nexus.listing.allListings")}
        </Link>
      </div>

      {/* Teaser block — public */}
      <section className="nx-container pb-10">
        <div className="grid gap-10 md:grid-cols-3">
          <div className="md:col-span-2">
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <span className={`nx-status ${status.cls}`}>
                <span className="dot" style={{ background: status.dot }} />
                {status.label}
              </span>
              {l.featured && <span className="nx-tag gold">Featured</span>}
              <span className="nx-tag">{regionLabel(l.region_group)}</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl leading-[1.1] text-white mb-6">{l.title}</h1>
            <div className="flex flex-wrap gap-2 mb-6">
              <span className="nx-tag">{l.jurisdiction}</span>
              {l.regulator && <span className="nx-tag">{l.regulator}</span>}
              <span className="nx-tag">{categoryLabel(l.category)}</span>
              {(l.tags || []).map((t) => (
                <span key={t} className="nx-tag">
                  {t}
                </span>
              ))}
            </div>
            <p className="text-white/85 text-lg leading-relaxed mb-8" data-testid="teaser-summary">
              {l.teaser_summary}
            </p>

            {l.highlights?.length > 0 && (
              <ul className="space-y-3 mb-10">
                {l.highlights.map((h, i) => (
                  <li key={i} className="text-white/80 pl-4 border-l-2 border-[#C9896E]/60">
                    → {h}
                  </li>
                ))}
              </ul>
            )}

            {/* Primary NDA CTA */}
            <div className="mt-8 border-t border-[var(--line)] pt-8" data-testid="listing-nda-block">
              <div className="nx-card" style={{ borderColor: "rgba(201,137,110,0.32)" }}>
                <div className="flex items-start gap-4">
                  <ShieldCheck size={22} className="text-[#C9896E] mt-1 shrink-0" />
                  <div className="flex-1">
                    <div className="nx-eyebrow mb-2">{t("nexus.listing.confidentialDetail")}</div>
                    <div className="font-serif text-xl text-white mb-2">{t("nexus.listing.priceUnderNDA")}</div>
                    <p className="text-[var(--bone-2)] mb-5 leading-relaxed">
                      {t("nexus.listing.releaseNote")}
                    </p>
                    <button
                      onClick={() => setNdaOpen(true)}
                      className="nx-btn-primary"
                      data-testid="listing-request-nda-btn"
                    >
                      {t("nexus.listing.requestFullDetailsCTA")} <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
              <p className="mt-4 text-xs text-[var(--bone-2)]/60 leading-relaxed">
                {t("nexus.listing.submissionNote")}
              </p>
              <p className="mt-2 text-xs text-[var(--bone-2)]/60">
                {t("nexus.listing.advisoryLine").split("{{link}}")[0]}
                <Link to="/services" className="underline hover:text-[var(--pewter)]">
                  {t("nexus.listing.corporaConsultants")}
                </Link>
                {t("nexus.listing.advisoryLine").split("{{link}}")[1] || ""}
              </p>
            </div>
          </div>

          <aside className="md:col-span-1">
            <div className="nx-card">
              <div className="nx-eyebrow mb-4">{t("nexus.listing.quickFacts")}</div>
              <dl className="space-y-3 text-sm">
                {l.year_established && <FactRow k={t("nexus.listing.established")} v={l.year_established} />}
                {l.regulatory_status && <FactRow k={t("nexus.listing.status")} v={l.regulatory_status} />}
                <FactRow k={t("nexus.listing.banking")} v={l.banking_included ? t("nexus.listing.bankingIncluded") : t("nexus.listing.bankingIntros")} />
                <FactRow k={t("nexus.listing.passporting")} v={l.passporting ? t("nexus.listing.yes") : t("nexus.listing.no")} />
                <FactRow k={t("nexus.listing.price")} v={t("nexus.listing.underNDA")} />
              </dl>
              <div className="nx-h-rule" />
              <div className="text-white/60 text-xs leading-relaxed">
                <ShieldCheck size={14} className="inline text-[#C9896E] mr-2" />
                {t("nexus.listing.everyEnquiry")}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Corpora advisory strip */}
      <section className="nx-container pb-16">
        <div
          className="nx-card flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
          style={{ borderColor: "rgba(201,137,110,0.25)" }}
        >
          <div className="flex items-start gap-4">
            <FileText size={22} className="text-[#C9896E] mt-1" />
            <div>
              <div className="nx-eyebrow mb-1">{t("nexus.listing.advisoryLayer")}</div>
              <div className="text-white/85 max-w-xl">
                {t("nexus.listing.advisoryLayerDesc")}
              </div>
            </div>
          </div>
          <Link to="/services" className="nx-btn-secondary">
            {t("nexus.listing.exploreAdvisory")}
          </Link>
        </div>
      </section>

      {/* Similar */}
      {data.similar?.length > 0 && (
        <section className="nx-container pb-24">
          <div className="nx-eyebrow mb-3">{t("nexus.listing.similar")}</div>
          <h2 className="font-serif text-2xl text-white mb-6">{t("nexus.listing.similarTitle")}</h2>
          <div className="grid gap-6 md:grid-cols-3">
            {data.similar.map((s) => (
              <ListingCard key={s.id} listing={s} />
            ))}
          </div>
        </section>
      )}

      {ndaOpen && (
        <NDARequestModal
          listing={l}
          onClose={() => setNdaOpen(false)}
        />
      )}
    </div>
  );
}

function FactRow({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-white/8 pb-2 last:border-none">
      <dt className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50">{k}</dt>
      <dd className="text-white/90 text-right">{v}</dd>
    </div>
  );
}

function NDARequestModal({ listing, onClose }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    full_name: "",
    company_name: "",
    registration_number: "",
    position: "",
    email: "",
    phone: "",
    accepted: false,
    website: "", // honeypot
  });
  const [sending, setSending] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [ndaMeta, setNdaMeta] = useState({ version: "", template: "" });

  useEffect(() => {
    nexusApi.get("/nda-template")
      .then((r) => setNdaMeta({ version: r.data.version, template: r.data.template }))
      .catch(() => {});
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const canSubmit =
    !!form.full_name.trim() &&
    !!form.company_name.trim() &&
    !!form.position.trim() &&
    !!form.email.trim() &&
    !!form.phone.trim() &&
    !!form.accepted &&
    !sending;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSending(true);
    try {
      const r = await nexusApi.post(`/listings/${listing.slug}/nda-request`, form);
      setConfirmation({
        reference: r.data.reference,
        accepted_at: r.data.accepted_at,
        email: form.email.trim(),
        nda_version: r.data.nda_version,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("nexus.nda.couldNotSend"));
    } finally {
      setSending(false);
    }
  };

  const listingRef = `NX / ${listing.slug}`;

  // Live-populate placeholders as the user types — legal clarity in the box.
  const renderedNDA = (() => {
    if (!ndaMeta.template) return "";
    const reg = form.registration_number.trim();
    const regSuffix = reg ? ` (Reg. no. ${reg})` : "";
    return ndaMeta.template
      .replace(/\[FULL NAME\]/g, form.full_name.trim() || "[FULL NAME]")
      .replace(/\[POSITION\]/g, form.position.trim() || "[POSITION]")
      .replace(/\[COMPANY NAME\]/g, form.company_name.trim() || "[COMPANY NAME]")
      .replace(/\[REG_SUFFIX\]/g, regSuffix)
      .replace(/\[EMAIL\]/g, form.email.trim() || "[EMAIL]")
      .replace(/\[LISTING REFERENCE\]/g, `${listing.title} · ${listingRef.replace("NX / ", "NX-").toUpperCase()}`)
      .replace(/\[TIMESTAMP\]/g, "on acceptance");
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(4,6,10,0.82)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="nda-modal"
    >
      <div
        className="w-full max-w-3xl max-h-[94vh] overflow-y-auto rounded-none border p-6 md:p-8"
        style={{ background: "#0D1323", borderColor: "rgba(201,137,110,0.32)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="nx-eyebrow mb-2" style={{ color: "#C9896E" }}>{t("nexus.nda.underNda")}</div>
            <div className="font-serif text-2xl text-white leading-tight">
              {t("nexus.nda.title")}
            </div>
            <div className="mt-1 text-xs text-[var(--bone-2)]/60">
              {listing.title}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -m-2 text-white/60 hover:text-white"
            aria-label={t("nexus.nda.close")}
            data-testid="nda-modal-close"
          >
            <X size={20} />
          </button>
        </div>

        {confirmation ? (
          <div className="text-center py-8" data-testid="nda-modal-confirmation">
            <CheckCircle2 size={44} className="text-[#7FB08A] mx-auto mb-5" />
            <div className="font-serif text-2xl text-white mb-4">
              {t("nexus.nda.confirmationHeading")}
            </div>
            <p className="text-[var(--bone-2)] leading-relaxed mb-4 max-w-md mx-auto">
              {t("nexus.nda.confirmationBody")}
            </p>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#C9896E] mb-1">
              {t("nexus.nda.reference")}: {confirmation.reference}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--bone-2)]/50 mb-6">
              {t("nexus.nda.ndaVersion")} {confirmation.nda_version}
            </div>
            <button onClick={onClose} className="nx-btn-secondary" data-testid="nda-modal-done">
              {t("nexus.nda.close")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-5" data-testid="nda-modal-form">
            <p className="text-[var(--bone-2)] text-sm leading-relaxed">
              {t("nexus.nda.intro")}
            </p>

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="nx-label">{t("nexus.nda.fullName")} *</label>
                <input
                  className="nx-field"
                  name="full_name"
                  required
                  value={form.full_name}
                  onChange={onChange}
                  data-testid="nda-full-name"
                />
              </div>
              <div>
                <label className="nx-label">{t("nexus.nda.position")} *</label>
                <input
                  className="nx-field"
                  name="position"
                  required
                  value={form.position}
                  onChange={onChange}
                  data-testid="nda-position"
                />
              </div>
              <div>
                <label className="nx-label">{t("nexus.nda.companyName")} *</label>
                <input
                  className="nx-field"
                  name="company_name"
                  required
                  value={form.company_name}
                  onChange={onChange}
                  data-testid="nda-company-name"
                />
              </div>
              <div>
                <label className="nx-label">{t("nexus.nda.registrationNumber")}</label>
                <input
                  className="nx-field"
                  name="registration_number"
                  value={form.registration_number}
                  onChange={onChange}
                  data-testid="nda-registration-number"
                />
              </div>
              <div>
                <label className="nx-label">{t("nexus.nda.email")} *</label>
                <input
                  className="nx-field"
                  name="email"
                  type="email"
                  required
                  value={form.email}
                  onChange={onChange}
                  data-testid="nda-email"
                />
              </div>
              <div>
                <label className="nx-label">{t("nexus.nda.phone")} *</label>
                <input
                  className="nx-field"
                  name="phone"
                  required
                  value={form.phone}
                  onChange={onChange}
                  data-testid="nda-phone"
                />
              </div>
              <div className="md:col-span-2">
                <label className="nx-label">{t("nexus.nda.listingReference")}</label>
                <input
                  className="nx-field"
                  readOnly
                  value={`${listing.title}  ·  ${listingRef}`}
                  data-testid="nda-listing-ref"
                />
              </div>
            </div>

            {/* NDA text — scrollable box, ~300px, cream on navy. English (Cyprus law). */}
            <div>
              <label className="nx-label flex items-center justify-between">
                <span>{t("nexus.nda.ndaBoxLabel")}</span>
                {ndaMeta.version && (
                  <span className="font-mono text-[9px] tracking-[0.2em] text-[var(--bone-2)]/50">
                    v{ndaMeta.version}
                  </span>
                )}
              </label>
              <p className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--bone-2)]/50 mb-2">
                {t("nexus.nda.legalNoticeEnglish")}
              </p>
              <div
                data-testid="nda-text-box"
                style={{
                  height: "300px",
                  overflowY: "auto",
                  background: "#F5EFE3",
                  color: "#1a1712",
                  border: "1px solid rgba(201,137,110,0.5)",
                  padding: "18px 20px",
                  fontFamily: "'Georgia', 'Liberation Serif', serif",
                  fontSize: "13px",
                  lineHeight: "1.65",
                  whiteSpace: "pre-wrap",
                  direction: "ltr",
                  textAlign: "left",
                }}
              >
                {renderedNDA || t("nexus.nda.loadingAgreement")}
              </div>
            </div>

            {/* honeypot */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
              <input
                tabIndex={-1}
                autoComplete="off"
                type="text"
                name="website"
                value={form.website}
                onChange={onChange}
              />
            </div>

            <label className="flex items-start gap-3 text-sm text-white/90 pt-1" data-testid="nda-accept-label">
              <input
                type="checkbox"
                name="accepted"
                checked={form.accepted}
                onChange={onChange}
                required
                className="mt-1 accent-[#C9896E]"
                data-testid="nda-accept"
              />
              <span>
                {t("nexus.nda.checkboxText")}
              </span>
            </label>

            <div>
              <button
                type="submit"
                className="nx-btn-primary w-full justify-center"
                disabled={!canSubmit}
                data-testid="nda-submit"
                style={!canSubmit ? { opacity: 0.5, cursor: "not-allowed" } : undefined}
              >
                {sending ? t("nexus.nda.sending") : (
                  <>
                    {t("nexus.nda.submitButton")} <ArrowRight size={14} />
                  </>
                )}
              </button>
              <p className="text-[11px] text-[var(--bone-2)]/60 mt-3 leading-relaxed text-center">
                {t("nexus.nda.logNote")}
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
